import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import Can from '../../components/Can';
import ConfirmModal from '../../components/ConfirmModal';
import './Employees.css';

const EMPTY = {
  emp_code: '', name: '', phone: '', designation: '',
  base_salary: '', ot_rate_per_hr: '', joined_on: '', is_active: true,
};

export default function Employees() {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const { data: employees, loading, error, reload } = useApi(() => api.employees.list(), []);

  const startEdit = (e) => {
    setForm({
      emp_code: e.emp_code, name: e.name, phone: e.phone || '',
      designation: e.designation || '',
      base_salary: e.base_salary || '', ot_rate_per_hr: e.ot_rate_per_hr || '',
      joined_on: e.joined_on || '',
      is_active: !!Number(e.is_active),
    });
    setEditId(e.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { notify('❌ Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_salary: parseFloat(form.base_salary || 0),
        ot_rate_per_hr: parseFloat(form.ot_rate_per_hr || 0),
        is_active: form.is_active ? 1 : 0,
      };
      if (editId) await api.employees.update(editId, payload);
      else await api.employees.create(payload);
      notify(editId ? '✅ Updated' : '✅ Employee added');
      setShowForm(false); setForm(EMPTY); setEditId(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.employees.remove(confirm.id);
      notify('✅ Deactivated');
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  return (
    <div className="em-page">
      <div className="page-header">
        <h2 className="page-title">Employees</h2>
        <Can module="employees" action="create">
          <button className="btn btn-primary btn-sm"
            onClick={() => { setShowForm(!showForm); setForm(EMPTY); setEditId(null); }}>
            {showForm ? '✕ Cancel' : '＋ New Employee'}
          </button>
        </Can>
      </div>

      {msg && <div className="em-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? 'Edit Employee' : 'Add New Employee'}</div>
          <div className="em-form-grid">
            <div className="form-group">
              <label>Employee Code {editId ? '' : '(Leave blank to auto-generate)'}</label>
              <input value={form.emp_code} disabled={!!editId}
                onChange={e => setForm({ ...form, emp_code: e.target.value.toUpperCase() })}
                placeholder="e.g. EMP001" />
            </div>
            <div className="form-group">
              <label>Name *</label>
              <input value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Designation</label>
              <input value={form.designation}
                onChange={e => setForm({ ...form, designation: e.target.value })}
                placeholder="Pump operator" />
            </div>
            <div className="form-group">
              <label>Base Salary (₹)</label>
              <input type="number" step="0.01" value={form.base_salary}
                onChange={e => setForm({ ...form, base_salary: e.target.value })} />
            </div>
            <div className="form-group">
              <label>OT Rate per hour (₹) *</label>
              <input type="number" step="0.01" value={form.ot_rate_per_hr}
                onChange={e => setForm({ ...form, ot_rate_per_hr: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Joined On *</label>
              <input type="date" value={form.joined_on}
                onChange={e => setForm({ ...form, joined_on: e.target.value })} />
            </div>
            {editId && (
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
                  <input type="checkbox" checked={form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                  Active
                </label>
              </div>
            )}
          </div>
          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ Saving...' : (editId ? 'Update' : 'Add Employee')}
            </button>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">Staff ({(employees || []).length})</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Code</th>
                <th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>Designation</th>
                <th style={{ textAlign: 'left' }}>Phone</th>
                <th>Base Salary</th>
                <th>OT Rate</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(employees || []).length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  No employees yet. Click "+ New Employee".
                </td></tr>
              ) : (employees || []).map(e => (
                <tr key={e.id} style={{ opacity: Number(e.is_active) === 0 ? 0.5 : 1 }}>
                  <td style={{ textAlign: 'left', fontFamily: 'monospace', fontWeight: 600 }}>{e.emp_code}</td>
                  <td style={{ textAlign: 'left' }}>{e.name}</td>
                  <td style={{ textAlign: 'left' }}>{e.designation || '—'}</td>
                  <td style={{ textAlign: 'left' }}>{e.phone || '—'}</td>
                  <td style={{ fontFamily: 'monospace' }}>
                    {Number(e.base_salary) > 0 ? `₹${fmtNum(e.base_salary)}` : '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace' }}>
                    ₹{fmtNum(e.ot_rate_per_hr)}/hr
                  </td>
                  <td>
                    <span className={`em-status ${Number(e.is_active) === 1 ? 'em-active' : 'em-inactive'}`}>
                      {Number(e.is_active) === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <Can module="employees" action="update">
                      <button className="btn btn-sm" onClick={() => startEdit(e)}>✏️</button>
                    </Can>
                    {Number(e.is_active) === 1 && (
                      <Can module="employees" action="delete">
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                          onClick={() => setConfirm(e)}>🗑</button>
                      </Can>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Deactivate Employee?"
        message={confirm ? `Deactivate "${confirm.name}"? Their OT history remains intact.` : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel="Deactivate"
      />
    </div>
  );
}
